import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import type { Usuario } from '@/types';

export function useAuth() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setAuth, clearAuth, user, isAuthenticated } = useAuthStore();

  const loginMutation = useMutation({
    mutationFn: ({ email, senha }: { email: string; senha: string }) =>
      authApi.login(email, senha),
    onSuccess: (response) => {
      const { token, user, requires2FA } = response.data;
      
      if (requires2FA) {
        return;
      }

      setAuth(user as Usuario, token);
      queryClient.setQueryData(['user'], user);
      
      // Redirecionamento baseado no cargo (role)
      if (user.role === 'ADMIN' || user.role === 'SUPERADMIN' || user.role === 'GERENTE') {
        navigate('/admin');
      } else {
        navigate('/modo-fabrica');
      }
    },
  });

  const loginOperadorMutation = useMutation({
    mutationFn: ({ matricula, pin }: { matricula: string; pin: string }) =>
      authApi.loginOperador(matricula, pin),
    onSuccess: (response) => {
      const { token, user } = response.data;
      setAuth(user as Usuario, token);
      navigate('/modo-fabrica');
    },
  });

  const verificar2FAMutation = useMutation({
    mutationFn: ({ userId, token }: { userId: string; token: string }) =>
      authApi.verificar2FA(userId, token),
    onSuccess: (response) => {
      const { token, user } = response.data;
      setAuth(user as Usuario, token);
      queryClient.setQueryData(['user'], user);
      
      // Redirecionamento baseado no cargo (role)
      if (user.role === 'ADMIN' || user.role === 'SUPERADMIN' || user.role === 'GERENTE') {
        navigate('/admin');
      } else {
        navigate('/modo-fabrica');
      }
    },
  });

  const logout = () => {
    clearAuth();
    queryClient.clear();
    navigate('/superlogin');
  };

  const meQuery = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const response = await authApi.me();
      return response.data as Usuario;
    },
    enabled: isAuthenticated,
    retry: false,
  });

  return {
    user,
    isAuthenticated,
    login: loginMutation.mutate,
    loginOperador: loginOperadorMutation.mutate,
    verificar2FA: verificar2FAMutation.mutate,
    logout,
    isLoading: loginMutation.isPending || loginOperadorMutation.isPending || verificar2FAMutation.isPending,
    error: loginMutation.error || loginOperadorMutation.error || verificar2FAMutation.error,
    loginData: loginMutation.data?.data,
    me: meQuery.data,
    isLoadingMe: meQuery.isLoading,
  };
}
